import { apiPost } from "~/shared/api/client";
import { putWithProgress } from "~/shared/lib/xhr-upload";

// Mirrors backend `StoragePathEnum` (ml-backend/app/core/enums.py). Keep in
// sync when the enum is extended; TS will surface any drift via the union.
export type StorageDestination =
    | "references"
    | "attachments"
    | "outputs"
    | "chat_attachments"
    | "review_attachments"
    | "review_versions"
    | "element_images"
    | "user_avatar"
    | "project_poster"
    | "project_teaser"
    | "project_file"
    | "board_attachments";

export const IMMUTABLE_CACHE_CONTROL = "public, max-age=31536000, immutable";

interface PresignResponseUrl {
    uploadUrl: string;
    s3Key: string;
    s3Url: string;
}

interface PresignResponse {
    urls: PresignResponseUrl[];
}

export interface PresignAndUploadArgs {
    destination: StorageDestination;
    kind: "image" | "video" | "audio";
    mimeType: string;
    originFilename: string;
    projectId?: string;
    /** Required for `attachments` (scene) destination — pairs with `projectId`. */
    sceneId?: string;
    /** Required for `board_attachments` destination. */
    boardId?: string;
}

export interface PresignAndUploadItem extends PresignAndUploadArgs {
    file: Blob | File;
}

export interface PresignAndUploadResult {
    s3Url: string;
    s3Key: string;
}

export async function presignAndUploadMany(items: PresignAndUploadItem[]): Promise<PresignAndUploadResult[]> {
    if (items.length === 0) return [];
    const presign = await apiPost<PresignResponse>("/v2/files/signed-urls", {
        files: items.map((it) => ({
            destination: it.destination,
            kind: it.kind,
            mime_type: it.mimeType,
            size_bytes: it.file.size,
            origin_filename: it.originFilename,
            project_id: it.projectId,
            scene_id: it.sceneId,
            board_id: it.boardId,
        })),
    });
    // Fail fast before firing any S3 PUTs — otherwise short presign response
    // wastes bandwidth on uploads we know will be discarded.
    if (presign.urls.length < items.length) {
        throw new Error("Empty presigned response");
    }
    return Promise.all(
        items.map(async (it, i) => {
            const signed = presign.urls[i];
            if (!signed?.uploadUrl) {
                throw new Error("Empty presigned response");
            }
            await putWithProgress(signed.uploadUrl, it.file, {
                mimeType: it.mimeType,
                cacheControl: IMMUTABLE_CACHE_CONTROL,
            });
            return { s3Url: signed.s3Url, s3Key: signed.s3Key };
        }),
    );
}

export async function presignAndUpload(
    file: Blob | File,
    args: PresignAndUploadArgs,
): Promise<PresignAndUploadResult> {
    const [result] = await presignAndUploadMany([{ file, ...args }]);
    if (!result) {
        throw new Error("Empty presigned response");
    }
    return result;
}
